import { useState, ChangeEvent, useRef, useLayoutEffect } from 'react';
import './ExtractComponents.css';
import RiskScorePanel from './RiskScorePanel';
import { validateISOCode } from '../utils/isoValidator';
import { useECTranslation, localizeResultText, localizeRestriction } from '../i18n';
import type { Component, RiskLevelColors, SanctionsIndicator, SanctionsMeasure, LicenseResponse, DualUseResult, ResultsState } from '../types';

function getAuthToken(): string | null {
    try {
        const raw = localStorage.getItem('auth-storage');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed.state?.token?.access_token || null;
    } catch { return null; }
}

// Textarea whose vertical length is exactly equal to its content. Manual
// resize is disabled — the height tracks the value, so an empty textarea is
// one row tall and grows line-by-line as the user types or pastes.
function AutoGrowTextarea(props: {
    value: string;
    onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    placeholder?: string;
    className?: string;
}) {
    const { value, onChange, placeholder, className } = props;
    const ref = useRef<HTMLTextAreaElement | null>(null);

    // useLayoutEffect avoids a flicker between the initial natural height
    // and the auto-sized height on first paint.
    useLayoutEffect(() => {
        const ta = ref.current;
        if (!ta) return;
        // Reset first so shrinking works after content is deleted, then
        // grow to whatever the content needs.
        ta.style.height = 'auto';
        ta.style.height = `${ta.scrollHeight}px`;
    }, [value]);

    return (
        <textarea
            ref={ref}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className={className}
            rows={1}
            style={{ overflow: 'hidden', resize: 'none' }}
        />
    );
}

type PdfProduct = {
    id: string;
    product_name: string;
    product_description: string;
};

// Manual product entry: one row per product, free-form description.
// Replaces the older single-textarea/quote-delimited input — each product
// is its own discrete field so paste-with-line-breaks is unambiguous.
type ManualProduct = {
    id: string;
    product_description: string;
};

const makeEmptyManualProduct = (): ManualProduct => ({
    id: crypto.randomUUID(),
    product_description: '',
});

function ExtractComponents() {
    const { t, lang } = useECTranslation();
    const [manualProducts, setManualProducts] = useState<ManualProduct[]>([
        makeEmptyManualProduct(),
    ]);
    const [file, setFile] = useState<File | null>(null);
    const [components, setComponents] = useState<Component[]>([]);
    // Editable products extracted from a PDF/image upload (new two-button
    // flow). Populated by uploadPDF, consumed by runComplianceOnPdfProducts.
    const [pdfProducts, setPdfProducts] = useState<PdfProduct[]>([]);
    const [pdfUploadId, setPdfUploadId] = useState<string | null>(null);
    const [pdfSessionId, setPdfSessionId] = useState<string | null>(null);
    const [destinationCountry, setDestinationCountry] = useState<string>('');
    const [countryOfOrigin, setCountryOfOrigin] = useState<string>('');
    const [shippingCountry, setShippingCountry] = useState<string>('');
    const [destinationError, setDestinationError] = useState<string>('');
    const [originError, setOriginError] = useState<string>('');
    const [shippingError, setShippingError] = useState<string>('');
    const [context, setContext] = useState<string>('');
    const [sanctionsIndicator, setSanctionsIndicator] = useState<SanctionsIndicator | null>(null);
    const [results, setResults] = useState<ResultsState | null>(null);
    // Incremented on every fresh compliance run so the results <section>
    // unmounts/remounts cleanly between runs (defensive against subtree
    // DOM state lingering from a prior render — opened <details> panels,
    // memoised children, etc.).
    const [resultKey, setResultKey] = useState<number>(0);
    const [loadingAction, setLoadingAction] = useState<
        'extract' | 'check-product' | 'check-components' | 'upload' | 'check-products' | null
    >(null);
    const isLoading = loadingAction !== null;
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    // True once "Run Compliance Check" or "Extract Components" has been clicked;
    // disables the "Add product" button until the form is cleared.
    const [actionTriggered, setActionTriggered] = useState<boolean>(false);

    // Legal condition checkboxes (from LicenseCheck module)
    const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
    const [isRepairReimport, setIsRepairReimport] = useState<boolean>(false);
    const [isTemporaryExhibition, setIsTemporaryExhibition] = useState<boolean>(false);
    const [isIntraGroupTransfer, setIsIntraGroupTransfer] = useState<boolean>(false);
    const [reimportWithin120Days, setReimportWithin120Days] = useState<boolean>(false);
    const [toCustomsFreeZone, setToCustomsFreeZone] = useState<boolean>(false);
    const [awareOfWmdUse, setAwareOfWmdUse] = useState<boolean>(false);
    const [informedByAuthority, setInformedByAuthority] = useState<boolean>(false);
    const [militaryEndUser, setMilitaryEndUser] = useState<boolean>(false);
    const [humanRightsConcern, setHumanRightsConcern] = useState<boolean>(false);

    // Helper function for risk level colors
    const getRiskLevelColor = (level: string): RiskLevelColors => {
        const colors: Record<string, RiskLevelColors> = {
            'MINIMAL': { bg: '#d1fae5', text: '#065f46', border: '#10b981' },
            'LOW': { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' },
            'MODERATE': { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
            'HIGH': { bg: '#fed7aa', text: '#9a3412', border: '#f97316' },
            'CRITICAL': { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' }
        };
        return colors[level] || colors['MODERATE'];
    };

    // Returns non-empty manual product descriptions, trimmed. Each one is
    // a discrete product — no delimiter parsing, no quote-wrapping fragility.
    const getCleanedManualProducts = (): string[] =>
        manualProducts.map(p => p.product_description.trim()).filter(Boolean);

    const extractComponents = async (): Promise<void> => {
        const cleaned = getCleanedManualProducts();
        if (cleaned.length === 0) {
            alert(t('extract.alertEnterOne'));
            return;
        }

        // Clear previous output (including any in-progress PDF flow)
        setResults(null);
        setError(null);
        setComponents([]);
        setPdfProducts([]);
        setPdfUploadId(null);
        setPdfSessionId(null);

        setActionTriggered(true);
        setLoadingAction('extract');

        try {
            const token = getAuthToken();
            // Loop per product so each call sees one discrete description.
            // Avoids the previous "wrap-in-quotes-and-split-on-regex" flow
            // that broke when descriptions contained quotes, commas, or
            // pasted line breaks.
            const aggregatedResults: any[] = [];
            for (const desc of cleaned) {
                // Backend still uses re.findall(r'"([^"]+)"', text) to pull
                // the product out, so wrap one description and neutralise any
                // embedded double quotes (this is a defensive transform; the
                // user already saw a clean description in their own row).
                const wrapped = `"${desc.replace(/"/g, "'")}"`;
                const response = await fetch('/extract-components', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                    body: JSON.stringify({
                        product_description: wrapped,
                    })
                });

                if (!response.ok) {
                    const err = await response.json();
                    throw new Error(typeof err?.detail === 'string' ? err.detail : JSON.stringify(err));
                }

                const data = await response.json();
                if (Array.isArray(data.results)) {
                    aggregatedResults.push(...data.results);
                }
            }

            // The LLM sometimes returns a "valid but empty" extraction
            // (e.g. when the description is too generic / non-technical /
            // too short). The HTTP call succeeds but no components come
            // back. Detect that and surface a useful message instead of
            // silently rendering nothing.
            const totalExtracted = aggregatedResults.reduce((sum: number, r: any) => {
                const e = r?.extracted_components || {};
                return sum
                    + (Array.isArray(e.components) ? e.components.length : 0)
                    + (Array.isArray(e.electronics) ? e.electronics.length : 0)
                    + (Array.isArray(e.sensors) ? e.sensors.length : 0)
                    + (Array.isArray(e.subsystems) ? e.subsystems.length : 0);
            }, 0);

            if (totalExtracted === 0) {
                setError(t('extract.errNoComponents'));
                return;
            }

            displayAll(aggregatedResults);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoadingAction(null);
        }
    };

    const displayAll = (resultsList: any[]): void => {
        const allComponents: Component[] = [];

        resultsList.forEach(item => {
            const extraction = item.extracted_components;
            const productName = item.product;

            extraction.components?.forEach((c: any) =>
                allComponents.push({ ...c, category: 'component', selected: true, productName })
            );

            extraction.electronics?.forEach((e: any) =>
                allComponents.push({
                    name: e.module,
                    description: e.function,
                    confidence: e.confidence,
                    category: 'electronics',
                    selected: true,
                    productName
                })
            );

            extraction.sensors?.forEach((s: any) =>
                allComponents.push({
                    name: s.sensor,
                    description: s.measures,
                    confidence: s.confidence,
                    category: 'sensor',
                    selected: true,
                    productName
                })
            );

            extraction.subsystems?.forEach((ss: any) =>
                allComponents.push({
                    name: ss.unit,
                    description: ss.role,
                    confidence: ss.confidence,
                    category: 'subsystem',
                    selected: true,
                    productName
                })
            );
        });

        setComponents(allComponents);
    };

    const addComponent = (productName: string = 'Manual/Other'): void => {
        setComponents([...components, {
            name: '',
            description: '',
            confidence: 'high',
            category: 'component',
            selected: true,
            productName: productName
        }]);
    };

    const removeComponent = (index: number): void => {
        setComponents(components.filter((_, i) => i !== index));
    };

    const updateComponent = (index: number, field: keyof Component, value: any): void => {
        const updated = [...components] as Component[];
        (updated[index] as any)[field] = value;
        updated[index].modified = true;
        setComponents(updated);
    };

    const runDualUse = async (): Promise<void> => {
        const cleanedManual = getCleanedManualProducts();
        if (cleanedManual.length === 0 || components.length === 0) {
            alert(t('extract.alertEnterAndSelect'));
            return;
        }

        const selectedComponents = components
            .filter(c => c.selected)
            .map(c => ({
                name: c.name,
                description: c.description,
                confidence: c.confidence,
                category: c.category,
                product_name: c.productName || null
            }));

        if (selectedComponents.length === 0) {
            alert(t('extract.alertSelectOne'));
            return;
        }

        // Clear prior results so the user doesn't see a stale compliance
        // verdict while the new check is in flight. Bumping resultKey forces
        // the results <section> to unmount/remount, dropping any subtree
        // state (open <details> panels, etc.) from the prior run.
        setResults(null);
        setError(null);
        setNotice(null);
        setSanctionsIndicator(null);
        setResultKey((k: number) => k + 1);
        setLoadingAction('check-components');

        try {
            const token = getAuthToken();
            const response = await fetch('/dual-use-check', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                body: JSON.stringify({
                    // Forward the UI language so the verdict text + retrieved
                    // regulation corpus come back in the selected language.
                    language: lang,
                    // Backend uses product_description only for record keeping;
                    // dual-use grouping is driven by product_name on each
                    // selected component. Join descriptions with blank lines
                    // so it stays readable in History.
                    product_description: cleanedManual.join('\n\n'),
                    selected_components: selectedComponents,
                    destination_country: destinationCountry.trim().toUpperCase() || null,
                    country_of_origin: countryOfOrigin.trim().toUpperCase() || null,
                    shipping_country: shippingCountry.trim().toUpperCase() || null,
                    export_context: context.trim() || null,
                    is_repair_reimport: isRepairReimport,
                    is_temporary_exhibition: isTemporaryExhibition,
                    is_intra_group_transfer: isIntraGroupTransfer,
                    reimport_within_120_days: reimportWithin120Days,
                    to_customs_free_zone: toCustomsFreeZone,
                    aware_of_wmd_use: awareOfWmdUse,
                    informed_by_authority: informedByAuthority,
                    military_end_user: militaryEndUser,
                    human_rights_concern: humanRightsConcern,
                })
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.detail || 'Unknown error');
            }

            const result = await response.json();

            const finalOutput: any = {
                isMulti: result.input_format === 'quoted_products',
                results: result.results || [result],
            };

            // Licence is run inline by the backend and returned in `result.license`
            // when there are flagged AL numbers + a destination country.
            if (result.license) {
                finalOutput.license = result.license;
            } else {
                const anyAlNumbers = (result.results || [result]).some(
                    (res: any) => (res.final_result?.al_numbers || []).length > 0,
                );
                if (anyAlNumbers && !destinationCountry.trim()) {
                    // Store the i18n key, not the resolved string, so the note
                    // re-translates live when the user switches language.
                    finalOutput.license_note = 'extract.noteLicenseSkipped';
                }
            }

            setResults(finalOutput);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoadingAction(null);
        }
    };

    const directDualUseCheck = async (): Promise<void> => {
        const cleanedManual = getCleanedManualProducts();
        if (cleanedManual.length === 0) {
            alert(t('extract.alertEnterOne'));
            return;
        }

        // Clear previous output (including any in-progress PDF flow)
        setResults(null);
        setError(null);
        setComponents([]);
        setPdfProducts([]);
        setPdfUploadId(null);
        setPdfSessionId(null);
        setResultKey((k: number) => k + 1);

        setActionTriggered(true);
        setLoadingAction('check-product');

        try {
            const token2 = getAuthToken();
            // Use /check-products — it natively takes a List[{product_name,
            // product_description}], so each row is one discrete product. No
            // quote-wrapping, no delimiter parsing. product_name is left empty
            // so the backend doesn't synthesise a "Product N" label into the
            // dual-use input or the persisted record.
            const products = cleanedManual.map((desc) => ({
                product_name: '',
                product_description: desc,
            }));
            const response = await fetch('/check-products', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token2 ? { 'Authorization': `Bearer ${token2}` } : {}) },
                body: JSON.stringify({
                    products,
                    language: lang,
                    top_k: 3,
                    destination_country: destinationCountry.trim().toUpperCase() || null,
                    country_of_origin: countryOfOrigin.trim().toUpperCase() || null,
                    shipping_country: shippingCountry.trim().toUpperCase() || null,
                    export_context: context.trim() || null,
                    is_repair_reimport: isRepairReimport,
                    is_temporary_exhibition: isTemporaryExhibition,
                    is_intra_group_transfer: isIntraGroupTransfer,
                    reimport_within_120_days: reimportWithin120Days,
                    to_customs_free_zone: toCustomsFreeZone,
                    aware_of_wmd_use: awareOfWmdUse,
                    informed_by_authority: informedByAuthority,
                    military_end_user: militaryEndUser,
                    human_rights_concern: humanRightsConcern,
                })
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.detail || 'Direct check failed');
            }

            const result = await response.json();

            const finalOutput: any = {
                // /check-products always returns a list of per-product
                // results, so render with the multi-result UI.
                isMulti: true,
                results: result.results || [],
            };

            // Licence is run inline by the backend and returned in `result.license`
            // when there are flagged AL numbers + a destination country.
            if (result.license) {
                finalOutput.license = result.license;
            } else {
                const hasAnyAl = finalOutput.results.some((res: any) => {
                    const arr = res.final_result?.al_numbers;
                    if (Array.isArray(arr) && arr.some((n: any) => typeof n === 'string' && n.trim())) {
                        return true;
                    }
                    const alnr = res.final_result?.alnr;
                    if (typeof alnr === 'string') {
                        const trimmed = alnr.trim().toLowerCase();
                        return trimmed && trimmed !== 'null' && trimmed !== 'unknown';
                    }
                    return false;
                });
                if (hasAnyAl && !destinationCountry.trim()) {
                    // Store the i18n key, not the resolved string, so the note
                    // re-translates live when the user switches language.
                    finalOutput.license_note = 'extract.noteLicenseSkipped';
                }
            }

            setResults(finalOutput);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoadingAction(null);
        }
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
        if (e.target.files?.[0]) {
            setFile(e.target.files[0]);
            setError(null);
        }
    };

    const uploadPDF = async (): Promise<void> => {
        if (!file) {
            setError(t('extract.errSelectFile'));
            return;
        }
        setNotice(null);

        // New two-button PDF flow: this step only extracts products +
        // LLM-composed descriptions. The user reviews/edits them in the
        // editable cards below, then clicks "Run Compliance Check" to
        // trigger /check-products. Clear any state from other flows so the
        // 2nd PDF doesn't render alongside leftovers from the 1st.
        setResults(null);
        setError(null);
        setNotice(null);
        setComponents([]);
        setPdfProducts([]);
        setPdfUploadId(null);
        setPdfSessionId(null);
        // Reset manual rows so the new PDF's auto-fill replaces the previous
        // PDF's summary instead of being skipped by the "is empty?" guard.
        setManualProducts([makeEmptyManualProduct()]);
        setLoadingAction('upload');

        const formData = new FormData();
        formData.append('file', file);
        if (destinationCountry.trim()) formData.append('destination_country', destinationCountry.trim().toUpperCase());
        if (countryOfOrigin.trim()) formData.append('country_of_origin', countryOfOrigin.trim().toUpperCase());
        if (shippingCountry.trim()) formData.append('shipping_country', shippingCountry.trim().toUpperCase());
        formData.append('is_repair_reimport', String(isRepairReimport));
        formData.append('is_temporary_exhibition', String(isTemporaryExhibition));
        formData.append('is_intra_group_transfer', String(isIntraGroupTransfer));
        formData.append('reimport_within_120_days', String(reimportWithin120Days));
        formData.append('to_customs_free_zone', String(toCustomsFreeZone));
        formData.append('aware_of_wmd_use', String(awareOfWmdUse));
        formData.append('informed_by_authority', String(informedByAuthority));
        formData.append('military_end_user', String(militaryEndUser));
        formData.append('human_rights_concern', String(humanRightsConcern));

        try {
            const token = getAuthToken();
            const response = await fetch('/upload-pdf-products', {
                method: 'POST',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                body: formData,
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.detail || 'Upload failed');
            }

            const data = await response.json();
            const products: PdfProduct[] = (data.products || []).map((p: any) => ({
                id: crypto.randomUUID(),
                product_name: (p.product_name || '').trim() || 'Product',
                product_description: (p.product_description || '').trim(),
            }));

            if (products.length === 0) {
                setError(t('extract.errNoProductsFile'));
            }

            setPdfProducts(products);
            setPdfUploadId(data.pdf_upload_id || null);
            setPdfSessionId(data.session_id || null);

            // Auto-fill the manual Product Description rows with a summary
            // of what the PDF contains. The manual rows aren't read by
            // /check-products in the PDF flow — they're just a visible
            // record of what was processed.
            const manualIsEmpty = manualProducts.every(p => !p.product_description.trim());
            if (manualIsEmpty) {
                const names = products.map(p => p.product_name).filter(Boolean);
                const summary = names.length > 0
                    ? names.join('\n')
                    : `Extracted from ${file.name}`;
                setManualProducts([
                    { id: crypto.randomUUID(), product_description: summary },
                ]);
            }

            if (data.extraction_metadata && data.extraction_metadata.method === 'ocr') {
                const warning = data.extraction_metadata.warning ||
                    `⚠️ OCR was used to extract text. Accuracy: ~${(data.extraction_metadata.ocr_confidence * 100).toFixed(0)}%`;
                setNotice(warning);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoadingAction(null);
        }
    };

    const updatePdfProduct = (id: string, field: keyof PdfProduct, value: string): void => {
        setPdfProducts(prev =>
            prev.map(p => (p.id === id ? { ...p, [field]: value } : p)),
        );
    };

    const removePdfProduct = (id: string): void => {
        setPdfProducts(prev => prev.filter(p => p.id !== id));
    };

    const addPdfProduct = (): void => {
        setPdfProducts(prev => [
            ...prev,
            { id: crypto.randomUUID(), product_name: '', product_description: '' },
        ]);
    };

    const runComplianceOnPdfProducts = async (): Promise<void> => {
        const cleaned = pdfProducts
            .map(p => ({
                product_name: p.product_name.trim(),
                product_description: p.product_description.trim(),
            }))
            .filter(p => p.product_name || p.product_description);

        if (cleaned.length === 0) {
            setError(t('extract.errNoProductsToCheck'));
            return;
        }

        setResults(null);
        setError(null);
        setResultKey((k: number) => k + 1);
        setLoadingAction('check-products');

        try {
            const token = getAuthToken();
            const response = await fetch('/check-products', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                body: JSON.stringify({
                    products: cleaned,
                    language: lang,
                    destination_country: destinationCountry.trim().toUpperCase() || null,
                    country_of_origin: countryOfOrigin.trim().toUpperCase() || null,
                    shipping_country: shippingCountry.trim().toUpperCase() || null,
                    export_context: context.trim() || null,
                    is_repair_reimport: isRepairReimport,
                    is_temporary_exhibition: isTemporaryExhibition,
                    is_intra_group_transfer: isIntraGroupTransfer,
                    reimport_within_120_days: reimportWithin120Days,
                    to_customs_free_zone: toCustomsFreeZone,
                    aware_of_wmd_use: awareOfWmdUse,
                    informed_by_authority: informedByAuthority,
                    military_end_user: militaryEndUser,
                    human_rights_concern: humanRightsConcern,
                    top_k: 3,
                    session_id: pdfSessionId,
                    pdf_upload_id: pdfUploadId,
                }),
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.detail || 'Compliance check failed');
            }

            const data = await response.json();
            const finalOutput: any = {
                isMulti: true,
                results: data.results || [],
            };
            if (data.license) {
                finalOutput.license = data.license;
            } else {
                const hasAnyAl = (data.results || []).some((res: any) => {
                    const arr = res.final_result?.al_numbers;
                    if (Array.isArray(arr) && arr.some((n: any) => typeof n === 'string' && n.trim())) {
                        return true;
                    }
                    const alnr = res.final_result?.alnr;
                    return typeof alnr === 'string' && alnr.trim() && alnr.trim().toLowerCase() !== 'null';
                });
                if (hasAnyAl && !destinationCountry.trim()) {
                    finalOutput.license_note = 'extract.noteLicenseSkippedShort';
                }
            }
            setResults(finalOutput);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoadingAction(null);
        }
    };

    // Manual product row helpers — discrete rows, not a delimited string.
    const addManualProduct = (): void => {
        setManualProducts(prev => [...prev, makeEmptyManualProduct()]);
    };

    const removeManualProduct = (id: string): void => {
        setManualProducts(prev => {
            const next = prev.filter(p => p.id !== id);
            // Always keep at least one row visible so the user has somewhere
            // to type after clearing the last entry.
            return next.length > 0 ? next : [makeEmptyManualProduct()];
        });
    };

    const updateManualProduct = (id: string, value: string): void => {
        setManualProducts(prev =>
            prev.map(p => (p.id === id ? { ...p, product_description: value } : p)),
        );
    };

    const clearAll = (): void => {
        setManualProducts([makeEmptyManualProduct()]);
        setFile(null);
        const fileInput = document.getElementById('pdf-upload-input') as HTMLInputElement | null;
        if (fileInput) fileInput.value = '';
        setComponents([]);
        setPdfProducts([]);
        setPdfUploadId(null);
        setPdfSessionId(null);
        setDestinationCountry('');
        setCountryOfOrigin('');
        setShippingCountry('');
        setDestinationError('');
        setOriginError('');
        setShippingError('');
        setContext('');
        setResults(null);
        setError(null);
        setNotice(null);
        setSanctionsIndicator(null);
        setActionTriggered(false);
        // Reset legal conditions
        setShowAdvanced(false);
        setIsRepairReimport(false);
        setIsTemporaryExhibition(false);
        setIsIntraGroupTransfer(false);
        setReimportWithin120Days(false);
        setToCustomsFreeZone(false);
        setAwareOfWmdUse(false);
        setInformedByAuthority(false);
        setMilitaryEndUser(false);
        setHumanRightsConcern(false);
    };

    // Check sanctions when destination country changes
    const checkSanctions = async (countryCode: string): Promise<void> => {
        if (!countryCode || countryCode.length !== 2) {
            setSanctionsIndicator(null);
            return;
        }

        try {
            const sToken = getAuthToken();
            const response = await fetch(`/sanctions/check/${countryCode.toUpperCase()}`, {
                headers: sToken ? { 'Authorization': `Bearer ${sToken}` } : {},
            });       
            if (response.ok) {
                const data = await response.json();
                setSanctionsIndicator(data);
            }
        } catch (err) {
            console.error('Failed to check sanctions:', err);
        }
    };

    // Handle destination country change with sanctions check
    const handleDestinationChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value.toUpperCase();
        setDestinationCountry(value);
        
        // Validate ISO code
        const validation = validateISOCode(value);
        setDestinationError(validation.error || '');
        
        // Check sanctions if valid
        if (value.length === 2 && !validation.error) {
            checkSanctions(value);
        } else {
            setSanctionsIndicator(null);
        }
    };

    return (
        <div className="extract-container">
            {/* Top heading removed — the module heading + tabs already title this view. */}

            {/* Country Information Section */}
            <section className="card">
                <h2>{t('extract.countryInfo')}</h2>
                <div className="country-fields">
                    <label>
                        {t('extract.destinationLabel')}
                        <input
                            type="text"
                            value={destinationCountry}
                            onChange={handleDestinationChange}
                            placeholder={t('extract.countryPlaceholder')}
                            maxLength={2}
                            className={`country-input ${destinationError ? 'input-error' : ''}`}
                        />
                        {destinationError ? (
                            <small style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                                {t('errors.invalidIso')}
                            </small>
                        ) : sanctionsIndicator && sanctionsIndicator.sanctioned ? (
                            <div className="sanctions-mini-alert">
                                <strong>{sanctionsIndicator.country_name}</strong> - {sanctionsIndicator.level.replace('_', ' ').toUpperCase()}
                            </div>
                        ) : (
                            <small style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                                {t('common.isoHelp')}
                            </small>
                        )}
                    </label>
                    <label>
                        {t('extract.originLabel')}
                        <input
                            type="text"
                            value={countryOfOrigin}
                            onChange={(e) => {
                                const value = e.target.value.toUpperCase();
                                setCountryOfOrigin(value);
                                if (value.trim()) {
                                    const validation = validateISOCode(value);
                                    setOriginError(validation.error || '');
                                } else {
                                    setOriginError('');
                                }
                            }}
                            placeholder={t('extract.countryPlaceholder')}
                            maxLength={2}
                            className={`country-input ${originError ? 'input-error' : ''}`}
                        />
                        {originError && (
                            <small style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                                {t('errors.invalidIso')}
                            </small>
                        )}
                    </label>
                    <label>
                        {t('extract.shippingLabel')}
                        <input
                            type="text"
                            value={shippingCountry}
                            onChange={(e) => {
                                const value = e.target.value.toUpperCase();
                                setShippingCountry(value);
                                if (value.trim()) {
                                    const validation = validateISOCode(value);
                                    setShippingError(validation.error || '');
                                } else {
                                    setShippingError('');
                                }
                            }}
                            placeholder={t('extract.countryPlaceholder')}
                            maxLength={2}
                            className={`country-input ${shippingError ? 'input-error' : ''}`}
                        />
                        {shippingError && (
                            <small style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                                {t('errors.invalidIso')}
                            </small>
                        )}
                    </label>
                </div>
            </section>

            {/* Legal Conditions Section (from LicenseCheck module) */}
            <section className="card">
                <div className="legal-conditions-section">
                    <div className="section-header" onClick={() => setShowAdvanced(!showAdvanced)}>
                        <h3>{t('cond.legalConditionsOptional')}</h3>
                        <span className={`toggle-icon ${showAdvanced ? 'open' : ''}`}>▼</span>
                    </div>

                    {showAdvanced && (
                        <div className="checkbox-sections">
                            {/* Transaction Type */}
                            <div className="checkbox-group">
                                <h4>{t('cond.transactionType')}</h4>
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={isRepairReimport}
                                        onChange={(e) => setIsRepairReimport(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.repair')}</strong>
                                        <small>{t('cond.repairHint')}</small>
                                    </span>
                                </label>
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={isTemporaryExhibition}
                                        onChange={(e) => setIsTemporaryExhibition(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.exhibition')}</strong>
                                        <small>{t('cond.exhibitionHint')}</small>
                                    </span>
                                </label>
                                {isTemporaryExhibition && (
                                    <label className="checkbox-label nested">
                                        <input
                                            type="checkbox"
                                            checked={reimportWithin120Days}
                                            onChange={(e) => setReimportWithin120Days(e.target.checked)}
                                        />
                                        <span className="checkbox-text">
                                            <strong>{t('cond.reimport120')}</strong>
                                            <small>{t('cond.reimport120Hint')}</small>
                                        </span>
                                    </label>
                                )}
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={isIntraGroupTransfer}
                                        onChange={(e) => setIsIntraGroupTransfer(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.intraGroup')}</strong>
                                        <small>{t('cond.intraGroupHint')}</small>
                                    </span>
                                </label>
                            </div>

                            {/* Destination Concerns */}
                            <div className="checkbox-group">
                                <h4>{t('cond.destinationConcerns')}</h4>
                                <label className="checkbox-label warning">
                                    <input
                                        type="checkbox"
                                        checked={toCustomsFreeZone}
                                        onChange={(e) => setToCustomsFreeZone(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.freeZone')}</strong>
                                        <small>{t('cond.freeZoneHint')}</small>
                                    </span>
                                </label>
                            </div>

                            {/* End-User/End-Use Concerns */}
                            <div className="checkbox-group">
                                <h4>{t('cond.endUserConcerns')}</h4>
                                <label className="checkbox-label warning">
                                    <input
                                        type="checkbox"
                                        checked={informedByAuthority}
                                        onChange={(e) => setInformedByAuthority(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.informedAuthority')}</strong>
                                        <small>{t('cond.informedAuthorityHint')}</small>
                                    </span>
                                </label>
                                <label className="checkbox-label warning">
                                    <input
                                        type="checkbox"
                                        checked={awareOfWmdUse}
                                        onChange={(e) => setAwareOfWmdUse(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.wmd')}</strong>
                                        <small>{t('cond.wmdHint')}</small>
                                    </span>
                                </label>
                                <label className="checkbox-label warning">
                                    <input
                                        type="checkbox"
                                        checked={militaryEndUser}
                                        onChange={(e) => setMilitaryEndUser(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.military')}</strong>
                                        <small>{t('cond.militaryHint')}</small>
                                    </span>
                                </label>
                                <label className="checkbox-label warning">
                                    <input
                                        type="checkbox"
                                        checked={humanRightsConcern}
                                        onChange={(e) => setHumanRightsConcern(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.humanRights')}</strong>
                                        <small>{t('cond.humanRightsHint')}</small>
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Step 1: Product Descriptions — one row per product. Each row
                is a discrete description so paste-with-line-breaks, commas,
                and quotes inside the text are all unambiguous. Replaces the
                earlier "wrap each product in quotes" textarea convention. */}
            <section className="card">
                <h2>{t('extract.productDescriptions')}</h2>
                <div className="manual-products-list">
                    {manualProducts.map((p, idx) => (
                        <div
                            key={p.id}
                            className="manual-product-row"
                            style={{
                                position: 'relative',
                                marginBottom: '0.75rem',
                                padding: '0.75rem',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                background: '#fafbfc',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: '0.5rem',
                                }}
                            >
                                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568' }}>
                                    {t('extract.product')} {idx + 1}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => removeManualProduct(p.id)}
                                    className="remove-btn"
                                    disabled={manualProducts.length === 1 && !p.product_description.trim()}
                                    title={t('common.removeProductTitle')}
                                >
                                    {t('common.remove')}
                                </button>
                            </div>
                            <AutoGrowTextarea
                                value={p.product_description}
                                onChange={(e) => updateManualProduct(p.id, e.target.value)}
                                placeholder={t('extract.productPlaceholder')}
                                className="product-input"
                            />
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={addManualProduct}
                    className="add-btn"
                    style={{ marginTop: '0.25rem' }}
                    disabled={actionTriggered || manualProducts.some(p => !p.product_description.trim())}
                    title={
                        actionTriggered
                            ? t('extract.clearToAddMore')
                            : manualProducts.some(p => !p.product_description.trim())
                                ? t('extract.fillExistingFirst')
                                : undefined
                    }
                >
                    {t('extract.addProduct')}
                </button>
                <div className="button-group" style={{ marginTop: '1rem' }}>
                    <button onClick={directDualUseCheck} disabled={isLoading || pdfProducts.length > 0 || !!destinationError || !!originError || !!shippingError} className="primary-btn">
                        {loadingAction === 'check-product' ? t('extract.checking') : t('extract.runCompliance')}
                    </button>
                    <button onClick={extractComponents} disabled={isLoading || pdfProducts.length > 0 || !!destinationError || !!originError || !!shippingError} className="primary-btn">
                        {loadingAction === 'extract' ? t('extract.extracting') : t('extract.extractComponents')}
                    </button>
                    <button onClick={clearAll} className="secondary-btn">{t('extract.clear')}</button>
                </div>
            </section>

            {/* Step 1b: Or Upload PDF / Image (alternative component source) */}
            <section className="card">
                <h2>{t('extract.orUpload')}</h2>
                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.85rem', color: '#666' }}>
                    {t('extract.uploadHint')}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    <input
                        type="file"
                        id="pdf-upload-input"
                        accept=".pdf,application/pdf,image/png,image/jpeg,image/tiff,image/bmp,image/webp,.png,.jpg,.jpeg,.tiff,.bmp,.webp"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                    <label
                        htmlFor="pdf-upload-input"
                        style={{
                            display: 'inline-block',
                            padding: '0.5rem 1rem',
                            background: '#f8f9fa',
                            border: '1px dashed #adb5bd',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#495057',
                            fontSize: '0.9rem',
                        }}
                    >
                        {file ? `📎 ${file.name}` : t('extract.chooseFile')}
                    </label>
                </div>
                <div className="button-group">
                    <button
                        onClick={uploadPDF}
                        disabled={isLoading || !file || !!destinationError || !!originError || !!shippingError}
                        className="primary-btn"
                    >
                        {loadingAction === 'upload' ? t('extract.uploading') : t('extract.uploadExtract')}
                    </button>
                </div>
            </section>

            {/* Step 1c: Extracted Products (PDF upload flow) — editable
                product descriptions composed by the LLM. User reviews/edits,
                then clicks "Run Compliance Check" to trigger /check-products. */}
            {pdfProducts.length > 0 && (
                <section className="card">
                    <h2>{t('extract.extractedProducts')}</h2>
                    <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#666' }}>
                        {t('extract.extractedProductsHint')}
                    </p>
                    <div className="components-list">
                        {pdfProducts.map((p) => (
                            <div key={p.id} className="component-item" style={{ marginBottom: '1rem' }}>
                                <div className="component-header" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="text"
                                        value={p.product_name}
                                        onChange={(e) => updatePdfProduct(p.id, 'product_name', e.target.value)}
                                        placeholder={t('extract.productNamePlaceholder')}
                                        className="component-name"
                                        style={{ flex: 1, fontWeight: 600 }}
                                    />
                                    <button
                                        onClick={() => removePdfProduct(p.id)}
                                        className="remove-btn"
                                        title={t('common.removeProductTitle')}
                                    >
                                        {t('common.remove')}
                                    </button>
                                </div>
                                <textarea
                                    value={p.product_description}
                                    onChange={(e) => updatePdfProduct(p.id, 'product_description', e.target.value)}
                                    rows={5}
                                    placeholder={t('extract.composedDescPlaceholder')}
                                    className="component-desc"
                                    style={{ width: '100%', marginTop: '0.5rem', fontFamily: 'inherit' }}
                                />
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={addPdfProduct}
                        className="add-btn"
                        style={{ marginTop: '0.5rem' }}
                    >
                        {t('extract.addProduct')}
                    </button>

                    <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '2px solid #F26F21' }}>
                        <button
                            onClick={runComplianceOnPdfProducts}
                            disabled={isLoading || !!destinationError || !!originError || !!shippingError}
                            className="primary-btn"
                            style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}
                        >
                            {loadingAction === 'check-products' ? t('extract.checking') : t('extract.runComplianceBolt')}
                        </button>
                    </div>
                </section>
            )}

            {/* Step 2: Component Selection */}
            {components.length > 0 && (
                <section className="card">
                    <h2>{t('extract.selectComponents')}</h2>
                    <div className="components-list">
                        {/* Group components by productName if it exists */}
                        {(() => {
                            const grouped = components.reduce((acc, comp) => {
                                const key = comp.productName || 'Manual/Other';
                                if (!acc[key]) acc[key] = [];
                                acc[key].push(comp);
                                return acc;
                            }, {} as Record<string, Component[]>);

                            return Object.keys(grouped).map(productName => (
                                <div key={productName} className="product-group" style={{ marginBottom: '1.5rem' }}>
                                    <h3 style={{ color: '#C95611', borderBottom: '1px solid #C95611', paddingBottom: '0.2rem' }}>
                                        {productName}
                                    </h3>
                                    {grouped[productName].map((comp, localIdx) => {
                                        // Find global index for update/remove
                                        const globalIndex = components.indexOf(comp);
                                        return (
                                            <div key={globalIndex} className="component-item">
                                                <div className="component-header">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={comp.selected}
                                                            onChange={(e) => updateComponent(globalIndex, 'selected', e.target.checked)}
                                                        />
                                                        <input
                                                            type="text"
                                                            value={comp.name}
                                                            onChange={(e) => updateComponent(globalIndex, 'name', e.target.value)}
                                                            placeholder={t('extract.componentNamePlaceholder')}
                                                            className="component-name"
                                                        />
                                                    </label>
                                                    {comp.modified && (
                                                        <span style={{
                                                            fontSize: '0.7rem',
                                                            background: '#fff3cd',
                                                            color: '#856404',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            marginLeft: '8px',
                                                            border: '1px solid #ffeeba'
                                                        }}>
                                                            {t('extract.modified')}
                                                        </span>
                                                    )}
                                                    <button onClick={() => removeComponent(globalIndex)} className="remove-btn">{t('common.remove')}</button>
                                                </div >
                                                <textarea
                                                    value={comp.description}
                                                    onChange={(e) => updateComponent(globalIndex, 'description', e.target.value)}
                                                    rows={3}
                                                    placeholder={t('extract.techDescPlaceholder')}
                                                    className="component-desc"
                                                />
                                                <div className="component-meta">
                                                    <label>
                                                        {t('extract.category')}
                                                        <select
                                                            value={comp.category}
                                                            onChange={(e) => updateComponent(globalIndex, 'category', e.target.value)}
                                                        >
                                                            <option value="component">{t('extract.cat.component')}</option>
                                                            <option value="electronics">{t('extract.cat.electronics')}</option>
                                                            <option value="sensor">{t('extract.cat.sensor')}</option>
                                                            <option value="subsystem">{t('extract.cat.subsystem')}</option>
                                                        </select>
                                                    </label>
                                                    <label>
                                                        {t('extract.confidence')}
                                                        <select
                                                            value={comp.confidence}
                                                            onChange={(e) => updateComponent(globalIndex, 'confidence', e.target.value)}
                                                        >
                                                            <option value="high">{t('extract.conf.high')}</option>
                                                            <option value="medium">{t('extract.conf.medium')}</option>
                                                            <option value="low">{t('extract.conf.low')}</option>
                                                        </select>
                                                    </label>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ));
                        })()}
                    </div >
                    <button onClick={() => addComponent()} className="add-btn">{t('extract.addGeneralComponent')}</button>

                    <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '2px solid #F26F21' }}>
                        <button onClick={runDualUse} disabled={isLoading || !!destinationError || !!originError || !!shippingError} className="primary-btn" style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}>
                            {loadingAction === 'check-components' ? t('extract.checking') : (
                                <>
                                    <span>{t('extract.runComplianceBolt')}</span>
                                    {components.some(c => c.modified) && <span style={{ fontSize: '0.8em', opacity: 0.9 }}>{t('extract.includesEdits')}</span>}
                                </>
                            )}
                        </button>
                    </div>
                </section >
            )}

            {/* Notice (non-fatal advisories, e.g. OCR confidence warning) */}
            {
                notice && (
                    <div
                        style={{
                            background: '#fff8e1',
                            border: '1px solid #ffe082',
                            color: '#7a5300',
                            padding: '0.75rem 1rem',
                            borderRadius: 4,
                            margin: '0.75rem 0',
                        }}
                    >
                        {notice}
                    </div>
                )
            }

            {/* Error Display */}
            {
                error && (
                    <div className="error-message">
                        <strong>{t('common.error')}</strong> {error}
                    </div>
                )
            }

            {/* Results */}
            {
                results && (
                    <section key={resultKey} className="card results-section">
                        <h2>{t('extract.resultsTitle')}</h2>

                        {results.isMulti ? (
                            <div className="multi-results">
                                {results.results.map((res, index) => (
                                    <div key={index} className="result-item-card" style={{ marginBottom: '2.5rem', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.5rem', background: '#fff' }}>
                                        <h3 style={{ color: '#4a90e2', fontSize: '1.25rem', marginBottom: '1rem', borderBottom: '2px solid #f0f4f8', paddingBottom: '0.5rem' }}>
                                            📦 {res.product_description}
                                        </h3>


                                        <div className="results-content">
                                            <pre style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                                                {JSON.stringify(res.final_result, (key, value) => key === 'trigger_keywords' ? undefined : value, 2)}
                                            </pre>
                                        </div>
                                        
                                        {(() => {
                                            // Matches can arrive in two shapes:
                                            //   - product-level (/dual-use-product): top-level res.matches
                                            //   - component-level (/dual-use-check, /extract-components):
                                            //     res.component_results[i].matches
                                            // Flatten either into a single list, dedupe by alnr keeping
                                            // the first occurrence (FAISS already returns by distance).
                                            const top = Array.isArray(res.matches) ? res.matches : [];
                                            const nested = Array.isArray((res as any).component_results)
                                                ? (res as any).component_results.flatMap((cr: any) =>
                                                    Array.isArray(cr?.matches) ? cr.matches : [])
                                                : [];
                                            const combined: any[] = top.length > 0 ? top : nested;
                                            const seen = new Set<string>();
                                            const matches = combined.filter((m: any) => {
                                                const key = String(m?.alnr || '');
                                                if (!key || seen.has(key)) return false;
                                                seen.add(key);
                                                return true;
                                            });
                                            if (matches.length === 0) return null;
                                            return (
                                                <div className="matches-section" style={{ marginTop: '1rem' }}>
                                                    <details style={{ background: '#f8f9fa', padding: '0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                                                        <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#4a5568', padding: '0.5rem' }}>
                                                            {t('extract.viewRegulation')}
                                                        </summary>
                                                        <div style={{ marginTop: '0.5rem', paddingLeft: '0.5rem' }}>
                                                            {matches.map((match: any, idx: number) => (
                                                                <div key={idx} style={{ marginBottom: '0.8rem', padding: '0.8rem', background: '#ffffff', borderRadius: '4px', borderLeft: '4px solid #007bff', fontSize: '0.9rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                                                                    <div style={{ fontWeight: 'bold', color: '#007bff', marginBottom: '0.3rem' }}>
                                                                        {match.alnr} <span style={{ color: '#666', fontWeight: 'normal', fontSize: '0.8em', marginLeft: '0.5rem' }}>({t('extract.similarity')}: {typeof match.distance === 'number' ? match.distance.toFixed(4) : '—'})</span>
                                                                    </div>
                                                                    {match.text && (
                                                                        <div style={{ color: '#333', fontFamily: 'Georgia, serif', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                                                                            {match.text}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </details>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <>
                                <div className="results-content">
                                    <pre>{JSON.stringify(results.dualUse, null, 2)}</pre>
                                </div>

                                {/* FAISS Matches Display */}
                                {results.matches && results.matches.length > 0 && (
                                    <div className="matches-section" style={{ marginTop: '1rem', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
                                        <h3>{t('extract.similarEntries')}</h3>
                                        {results.matches.map((match, idx) => (
                                            <div key={idx} style={{ marginBottom: '0.8rem', padding: '0.5rem', background: '#f8f9fa', borderRadius: '4px', fontSize: '0.9rem' }}>
                                                <div style={{ fontWeight: 'bold', color: '#007bff' }}>
                                                    ALNR: {match.alnr} <span style={{ color: '#666', fontWeight: 'normal', marginLeft: '0.5rem' }}>(Distance: {match.distance.toFixed(4)})</span>
                                                </div>
                                                {/* <div style={{ marginTop: '0.25rem', color: '#333' }}>{match.text}</div> */}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                )
            }

            {
                results && results.license && (
                    <section className="card license-section">
                        <h2>{t('extract.licenseTitle')}</h2>

                        {results.license.agent_result && results.license.rules_result ? (
                            <>
                                {results.license.agent_result.authorisation !== 'Skipped' && (
                                    <div className="result-header-dual">
                                        <div className={`match-badge ${results.license.match ? 'match' : 'mismatch'}`}>
                                            {results.license.match ? t('extract.resultsMatch') : t('extract.resultsDiffer')}
                                        </div>
                                    </div>
                                )}

                                {results.license.jurisdiction && (
                                    <div className="jurisdiction-analysis-card" style={{ marginBottom: '1.5rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.25rem' }}>
                                        <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#334155', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {t('extract.jurisdiction')}
                                        </h3>
                                        <div className="jurisdiction-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                                            {Object.entries(results.license.jurisdiction.reasoning).map(([axis, reason]) => (
                                                <div key={axis} className="axis-item" style={{ padding: '0.75rem', background: 'white', borderRadius: '8px', borderLeft: '4px solid #F26F21', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                                    <div style={{ fontWeight: 'bold', fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', marginBottom: '0.25rem' }}>{axis}</div>
                                                    <div style={{ fontSize: '0.9rem', color: '#1e293b', lineHeight: '1.4' }}>{reason}</div>
                                                </div>
                                            ))}
                                        </div>
                                        {results.license.jurisdiction.warnings.length > 0 && (
                                            <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#fff1f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                                                {results.license.jurisdiction.warnings.map((w, i) => (
                                                    <div key={i} style={{ color: '#991b1b', fontWeight: 'bold', fontSize: '0.85rem' }}>⚠️ {w}</div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="dual-result-grid" style={{ gridTemplateColumns: results.license.agent_result.authorisation === 'Skipped' ? '1fr' : '1fr 1fr' }}>
                                    {/* Deterministic Rules Column */}
                                    <div className={`result-card dual-card ${results.license.rules_result.license_required ? 'license-required' : 'license-not-required'}`}>
                                        <div className="card-header">
                                            <h3>{t('extract.deterministicRules')}</h3>
                                            <span className="badge rule-badge">{t('extract.codeDriven')}</span>
                                        </div>

                                        <div className="result-item">
                                            <span className="result-label">{t('extract.licenseRequired')}</span>
                                            <span className={`result-value ${results.license.rules_result.license_required ? 'required' : 'not-required'}`}>
                                                {results.license.rules_result.license_required ? t('common.yes') : t('common.no')}
                                            </span>
                                        </div>

                                        {results.license.rules_result.authorisation && (
                                            <div className="result-item">
                                                <span className="result-label">{t('extract.authorisation')}</span>
                                                <span className="result-value authorisation">{results.license.rules_result.authorisation}</span>
                                            </div>
                                        )}

                                        <div className="result-item full-width">
                                            <span className="result-label">{t('extract.legalJustification')}</span>
                                            <span className="result-value reason">{localizeResultText(results.license.rules_result.reason, lang)}</span>
                                        </div>

                                        {results.license.rules_result.product_reasoning && (
                                            <div className="result-item full-width">
                                                <span className="result-label">{t('extract.whyRestricted')}</span>
                                                <span className="result-value reason">{localizeResultText(results.license.rules_result.product_reasoning, lang)}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* AI Agent Column - Only show if not skipped */}
                                    {results.license.agent_result.authorisation !== 'Skipped' && (
                                        <div className={`result-card dual-card ${results.license.agent_result.license_required ? 'license-required' : 'license-not-required'}`}>
                                            <div className="card-header">
                                                <h3>{t('extract.aiAgent')}</h3>
                                                <span className="badge agent-badge">{t('extract.llmDriven')}</span>
                                            </div>

                                            <div className="result-item">
                                                <span className="result-label">{t('extract.licenseRequired')}</span>
                                                <span className={`result-value ${results.license.agent_result.license_required ? 'required' : 'not-required'}`}>
                                                    {results.license.agent_result.license_required ? t('common.yes') : t('common.no')}
                                                </span>
                                            </div>

                                            {results.license.agent_result.authorisation && (
                                                <div className="result-item">
                                                    <span className="result-label">{t('extract.authorisation')}</span>
                                                    <span className="result-value authorisation">{results.license.agent_result.authorisation}</span>
                                                </div>
                                            )}

                                            <div className="result-item full-width">
                                                <span className="result-label">{t('extract.reasoning')}</span>
                                                <span className="result-value reason">{localizeResultText(results.license.agent_result.reason, lang)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {(results.license.rules_result.legal_reference || results.license.agent_result.legal_reference) && (
                                    <div className="explanation-card">
                                        <h3>{t('extract.legalReference')}</h3>
                                        <p className="explanation-text">
                                            {results.license.rules_result.legal_reference || results.license.agent_result.legal_reference}
                                        </p>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className={`license-result ${results.license.license_required ? 'required' : 'not-required'}`}>
                                <p><strong>{t('extract.licenseRequired')}</strong> {results.license.license_required ? t('common.yes') : t('common.no')}</p>
                                {results.license.authorisation && (
                                    <p><strong>{t('extract.authorisation')}</strong> {results.license.authorisation}</p>
                                )}
                                <p><strong>{t('license.reason')}</strong> {localizeResultText(results.license.reason, lang)}</p>
                            </div>
                        )}

                        {/* Sanctions Information */}
                        {results.license && results.license.sanctions_info && (
                            <div className={`sanctions-alert-card ${results.license.sanctions_info.sanctioned ? 'sanctioned' : 'clear'}`}>
                                <div className="sanctions-header">
                                    <h3>
                                        {results.license.sanctions_info.sanctioned ? t('extract.sanctionsAlert') : t('extract.sanctionsCheck')}
                                    </h3>
                                    <span className={`sanctions-level-badge level-${results.license.sanctions_info.level}`}>
                                        {results.license.sanctions_info.level.replace('_', ' ').toUpperCase()}
                                    </span>
                                </div>
                                
                                <div className="sanctions-details">
                                    <div className="sanctions-row">
                                        <strong>{t('extract.destination')}</strong>
                                        <span>{results.license.sanctions_info.country_name} ({destinationCountry})</span>
                                    </div>
                                    
                                    {results.license.sanctions_info.sanctioned && (
                                        <>
                                            {results.license.sanctions_info.restrictions && results.license.sanctions_info.restrictions.length > 0 && (
                                                <div className="sanctions-row">
                                                    <strong>{t('extract.restrictions')}</strong>
                                                    <div className="restrictions-tags">
                                                        {results.license.sanctions_info.restrictions.map((r, i) => (
                                                            <span key={i} className="restriction-tag">{localizeRestriction(r, lang)}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Live restrictive measures from the EU Sanctions Map, filtered to
                                                the ones the licence engine matched to THIS product (falls back to
                                                all measures for the destination if none matched specifically). */}
                                            {(() => {
                                                const si = results.license?.sanctions_info;
                                                if (!si) return null;
                                                // Prefer the German-translated matched measures (from the same LLM call).
                                                const localized = lang === 'de' ? (si.matched_measures_localized || []) : [];
                                                let shown: SanctionsMeasure[];
                                                let productSpecific: boolean;
                                                if (localized.length) {
                                                    shown = localized;
                                                    productSpecific = true;
                                                } else {
                                                    const all = si.measures || [];
                                                    const matched = si.matched_measures || [];
                                                    const isRelevant = (m: SanctionsMeasure): boolean => matched.some((mm) => {
                                                        const tt = (m.title || '').toLowerCase().trim();
                                                        const s = mm.toLowerCase();
                                                        return !!tt && (s === tt || s.startsWith(tt) || s.includes(tt));
                                                    });
                                                    const relevant = matched.length ? all.filter(isRelevant) : [];
                                                    productSpecific = relevant.length > 0;
                                                    shown = productSpecific ? relevant : all;
                                                }
                                                if (!shown.length) return null;
                                                return (
                                                    <div className="sanctions-warnings">
                                                        <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.35rem' }}>
                                                            {t(productSpecific ? 'extract.relevantMeasures' : 'extract.allMeasures')}
                                                        </div>
                                                        {shown.map((m, i) => (
                                                            <div key={i} className="warning-item">
                                                                <strong>{m.title}</strong>
                                                                {m.description && (
                                                                    <div style={{ fontWeight: 400, fontSize: '0.85rem', marginTop: '0.15rem', color: '#555' }}>
                                                                        {m.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}

                                            {results.license.sanctions_info.sources && results.license.sanctions_info.sources.length > 0 && (
                                                <div className="sanctions-row sanctions-sources">
                                                    <strong>{t('extract.sources')}</strong>
                                                    <ul className="sanctions-sources-list">
                                                        {results.license.sanctions_info.sources.map((s, i) => (
                                                            <li key={i}>
                                                                <a href={s.url} target="_blank" rel="noopener noreferrer">
                                                                    {s.title}
                                                                </a>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    
                                    {!results.license.sanctions_info.sanctioned && (
                                        <div className="sanctions-clear-message">
                                            {t('extract.noSanctions')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </section >
                )
            }

            {results && results.license && results.license.risk_assessment && (
                <section className="card">
                    <h3 className="risk-section-title">{t('extract.riskAssessment')}</h3>
                    
                    <div className="risk-dual-view">
                        {/* COMMENTED OUT: Unified Risk Score (1-5) - Management View
                        <div className="unified-risk-compact">
                            <div className="unified-risk-header">
                                <h4>Management Summary (1-5 Scale)</h4>
                                <span className="scale-badge-small">Unified Scale</span>
                            </div>
                            
                            {results.license.unified_risk && (
                                <>
                                    <div 
                                        className="risk-score-compact"
                                        style={{
                                            backgroundColor: getRiskLevelColor(results.license.unified_risk.risk_level).bg,
                                            borderColor: getRiskLevelColor(results.license.unified_risk.risk_level).border
                                        }}
                                    >
                                        <span className="risk-icon-large">{results.license.unified_risk.icon}</span>
                                        <div className="risk-score-info">
                                            <span 
                                                className="risk-score-number-compact"
                                                style={{ color: getRiskLevelColor(results.license.unified_risk.risk_level).text }}
                                            >
                                                {results.license.unified_risk.risk_score}/5
                                            </span>
                                            <span 
                                                className="risk-level-label-compact"
                                                style={{ color: getRiskLevelColor(results.license.unified_risk.risk_level).text }}
                                            >
                                                {results.license.unified_risk.risk_level}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="risk-details-compact">
                                        <div className="risk-detail-item">
                                            <strong>Action:</strong>
                                            <span>{results.license.unified_risk.action_required}</span>
                                        </div>
                                        <div className="risk-detail-item">
                                            <strong>Approval:</strong>
                                            <span>{results.license.unified_risk.approval_level}</span>
                                        </div>
                                        {results.license.unified_risk.key_factors && results.license.unified_risk.key_factors.length > 0 && (
                                            <div className="risk-detail-item">
                                                <strong>Key Factors:</strong>
                                                <ul className="key-factors-compact">
                                                    {results.license.unified_risk.key_factors.slice(0, 3).map((factor, idx) => (
                                                        <li key={idx}>{factor}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                        */}
                        
                        {/* Detailed Risk Score (0-100) - Now the only view */}
                        <div className="detailed-risk-compact">
                            <RiskScorePanel riskData={results.license.risk_assessment} />
                        </div>
                    </div>
                </section>
            )}

            {
                results && results.licenseError && (
                    <section className="card error-card">
                        <h2>{t('extract.licenseErrorTitle')}</h2>
                        <p className="license-error">{results.licenseError}</p>
                    </section>
                )
            }

            {
                results && results.license_note && (
                    <section className="card" style={{ background: '#fff3cd', border: '2px solid #ffc107' }}>
                        <h2>{t('extract.licenseNoteTitle')}</h2>
                        <p style={{ color: '#856404', fontSize: '1rem', margin: 0 }}>{t(results.license_note)}</p>
                    </section>
                )
            }

            {/* Legal Disclaimer */}
            {results && (
                <div className="disclaimer-banner" style={{
                    marginTop: '2rem',
                    padding: '1rem 1.5rem',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    border: '1px solid #dee2e6',
                    borderLeft: '4px solid #6c757d',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    color: '#495057',
                    lineHeight: '1.6'
                }}>
                    <p style={{ margin: '0 0 0.25rem 0', fontWeight: '600', color: '#343a40' }}>
                        {t('extract.disclaimerTitle')}
                    </p>
                    <p style={{ margin: 0, fontStyle: 'italic' }}>
                        {t('extract.disclaimerBody')}
                    </p>
                </div>
            )}
        </div >
    );
}

export default ExtractComponents;